import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, StatusBadge, TStatusBadgeProps, RectangleSkeleton } from '@treezor/design-system';

import { IAdditionalVigilanceDetailModalProps } from '../modals/AdditionalVigilanceDetailModal';

import { InfoTooltip } from '@/ui-kit/Tips/InfoTooltip';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';

import { openModal } from '@/store/actions/core.actions';

import { selectProfileKYCByUserId } from '@/store/selectors';

import { useKYCIndications } from '@/features/kyc/hooks/useKycIndications';

interface IKycVigilanceMeasureProps {
  userId: number;
  validationDate?: Date;
}

export const KycVigilanceMeasure = ({ userId, validationDate }: IKycVigilanceMeasureProps) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const allPayins = useAppSelector((state) => selectProfileKYCByUserId(state, userId)?.state?.payins?.state)?.filter(
    (payin) => (validationDate ? new Date(payin?.createdDate) < validationDate : true),
  );

  const isLoading = useAppSelector((state) => selectProfileKYCByUserId(state, userId)?.state?.payins?.isLoading);
  const { settledPayins, pendingPayins } = useKYCIndications(allPayins);

  const statusProps: Pick<TStatusBadgeProps, 'color' | 'label'> = useMemo(() => {
    if (settledPayins?.length > 0)
      return { label: t('pages.currentProfiles.kyc.vigilance.status.settled'), color: 'green' };

    if (pendingPayins?.length > 0)
      return { label: t('pages.currentProfiles.kyc.vigilance.status.pending'), color: 'orange' };
    return { label: t('pages.currentProfiles.kyc.vigilance.status.none'), color: 'gray' };
  }, [pendingPayins, settledPayins, t]);

  const toggleAdditionalVigilanceDialog = () =>
    dispatch(
      openModal<IAdditionalVigilanceDetailModalProps>('additionalVigilanceDetailModal', { payins: settledPayins }),
    );

  return (
    <div className="flex flex-col gap-5 items-start h-full">
      <div className="flex flex-col gap-2 items-start">
        <div className="flex items-center gap-2">
          <h6 className="typo-h6-bold text-secondary">{t('pages.currentProfiles.kyc.vigilance.header')}</h6>
          <InfoTooltip
            tooltipInfo={t('pages.currentProfiles.kyc.vigilance.tooltip')}
            infoClassName="min-w-[150px]"
            className="mt-1"
          />
        </div>
        {isLoading ? (
          <RectangleSkeleton width={80} height={20} margin="0" />
        ) : (
          <Button icon="chevron_right" iconPosition="right" variant="ghost" onClick={toggleAdditionalVigilanceDialog}>
            {t('pages.currentProfiles.kyc.actions.seeDetails')}
          </Button>
        )}
      </div>
      <div className="flex items-center gap-4">
        <p className="typo-body-regular-paragraph text-secondary">
          {t('pages.currentProfiles.kyc.vigilance.status.title')}
        </p>
        {isLoading ? (
          <RectangleSkeleton width={120} height={20} margin="0" />
        ) : (
          <div className="flex items-center gap-2">
            <StatusBadge {...statusProps} emphasis="low" />
          </div>
        )}
      </div>
    </div>
  );
};
